import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFestivePrice } from '../utils/festivePrice';

const API = process.env.REACT_APP_API_URL || '';
const authH = () => {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

interface Slab {
  id?: string; minAmount: number; maxAmount: number;
  cashbackPercentage: number; description: string; active: boolean; priority: number;
}
const emptySlab = (): Slab => ({ minAmount: 0, maxAmount: 0, cashbackPercentage: 0, description: '', active: true, priority: 0 });

const AdminDashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [adminTab, setAdminTab] = useState<'overview' | 'cashback' | 'pricing' | 'report'>('overview');

  // Stats
  const [stats, setStats] = useState<any>(null);

  // Cashback slab management
  const [slabs, setSlabs] = useState<Slab[]>([]);
  const [slabForm, setSlabForm] = useState<Slab>(emptySlab());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [slabMsg, setSlabMsg] = useState<string>('');

  // Wallet report
  const [report, setReport] = useState<any>(null);

  // Festive pricing
  const [sellingInput, setSellingInput] = useState('');
  const [previewPrice, setPreviewPrice] = useState<ReturnType<typeof getFestivePrice> | null>(null);
  const [customOriginal, setCustomOriginal] = useState('');

  useEffect(() => {
    if (user?.role === 'admin') {
      fetch(`${API}/api/admin/stats`, { headers: authH() }).then(r => r.json()).then(setStats).catch(() => {});
      loadSlabs();
    }
  }, [user]);

  const loadSlabs = () => {
    fetch(`${API}/api/admin/cashback-slabs`, { headers: authH() })
      .then(r => r.json()).then(setSlabs).catch(() => {});
  };
  const loadReport = () => {
    fetch(`${API}/api/admin/wallet-report`, { headers: authH() })
      .then(r => r.json()).then(setReport).catch(() => {});
  };

  const handleSaveSlab = async () => {
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId ? `${API}/api/admin/cashback-slabs/${editingId}` : `${API}/api/admin/cashback-slabs`;
    const res = await fetch(url, { method, headers: authH(), body: JSON.stringify(slabForm) });
    if (res.ok) { setSlabMsg('Saved!'); setSlabForm(emptySlab()); setEditingId(null); loadSlabs(); }
    else setSlabMsg('Error saving slab');
    setTimeout(() => setSlabMsg(''), 3000);
  };
  const handleDeleteSlab = async (id: string) => {
    if (!window.confirm('Delete this slab?')) return;
    await fetch(`${API}/api/admin/cashback-slabs/${id}`, { method: 'DELETE', headers: authH() });
    loadSlabs();
  };

  const handlePreview = () => {
    const sp = parseInt(sellingInput, 10);
    if (isNaN(sp) || sp <= 0) return;
    const fp = getFestivePrice(sp * 3 + Date.now() % 100);
    setPreviewPrice({ ...fp, sellingPrice: sp, amountSaved: fp.originalPrice - sp, discountPercent: Math.round(((fp.originalPrice - sp) / fp.originalPrice) * 100) });
    setCustomOriginal(String(fp.originalPrice));
  };

  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <nav className="bg-[#1A1A2E] text-white h-[60px] flex items-center justify-between px-6">
        <div className="flex items-center space-x-3">
          <span className="font-bold text-lg">Sasta Fashions — Admin</span>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-white/70">{user.email}</span>
          <button onClick={logout} className="text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg">Logout</button>
        </div>
      </nav>

      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-1">Dashboard</h1>
        <p className="text-gray-500 mb-6">Welcome back, {user.name}</p>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Users', value: stats?.totalUsers ?? '—' },
            { label: 'Wallet Balance', value: stats ? `₹${Number(stats.totalWalletBalance).toFixed(0)}` : '—' },
            { label: 'Cashback Given', value: stats ? `₹${Number(stats.totalCashbackGiven).toFixed(0)}` : '—' },
            { label: 'Total Recharges', value: stats ? `₹${Number(stats.totalRechargeAmount).toFixed(0)}` : '—' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <p className="text-xs text-gray-500 mb-1">{s.label}</p>
              <p className="text-2xl font-bold text-[#1A1A2E]">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {([['overview','Overview'],['cashback','Cashback Slabs'],['pricing','Pricing Calc'],['report','Wallet Report']] as const).map(([key, label]) => (
            <button key={key} onClick={() => { setAdminTab(key); if (key === 'report') loadReport(); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${adminTab === key ? 'border-[#E91E63] text-[#E91E63]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {adminTab === 'overview' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <p className="text-gray-500 text-sm">Select a tab above to manage cashback slabs, pricing, or view wallet reports.</p>
          </div>
        )}

        {/* ── CASHBACK SLABS ── */}
        {adminTab === 'cashback' && (
          <div className="space-y-6">
            {/* Slab Form */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4">{editingId ? 'Edit Slab' : 'Add New Cashback Slab'}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                {[
                  { label: 'Min Amount (₹)', key: 'minAmount', type: 'number' },
                  { label: 'Max Amount (₹)', key: 'maxAmount', type: 'number' },
                  { label: 'Cashback %', key: 'cashbackPercentage', type: 'number' },
                  { label: 'Priority', key: 'priority', type: 'number' },
                  { label: 'Description', key: 'description', type: 'text' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                    <input type={f.type} value={(slabForm as any)[f.key]}
                      onChange={e => setSlabForm(s => ({ ...s, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DAA520] focus:outline-none" />
                  </div>
                ))}
                <div className="flex items-center gap-2 mt-5">
                  <input type="checkbox" id="slabActive" checked={slabForm.active}
                    onChange={e => setSlabForm(s => ({ ...s, active: e.target.checked }))} />
                  <label htmlFor="slabActive" className="text-sm text-gray-700">Active</label>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={handleSaveSlab} className="bg-[#DAA520] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#c5941c]">
                  {editingId ? 'Update Slab' : 'Add Slab'}
                </button>
                {editingId && <button onClick={() => { setEditingId(null); setSlabForm(emptySlab()); }} className="border border-gray-300 px-4 py-2 rounded-lg text-sm">Cancel</button>}
                {slabMsg && <span className="text-sm text-green-600 font-medium mt-2">{slabMsg}</span>}
              </div>
            </div>

            {/* Existing slabs */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>{['Min','Max','Cashback %','Priority','Description','Status','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {slabs.map((s: any) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">₹{s.minAmount}</td>
                      <td className="px-4 py-3">₹{s.maxAmount >= 999999 ? '5000+' : s.maxAmount}</td>
                      <td className="px-4 py-3 font-bold text-purple-600">{s.cashbackPercentage}%</td>
                      <td className="px-4 py-3">{s.priority}</td>
                      <td className="px-4 py-3 text-gray-500">{s.description}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {s.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 flex gap-2">
                        <button onClick={() => { setEditingId(s.id); setSlabForm(s); }} className="text-blue-600 hover:underline text-xs">Edit</button>
                        <button onClick={() => handleDeleteSlab(s.id)} className="text-red-500 hover:underline text-xs">Delete</button>
                      </td>
                    </tr>
                  ))}
                  {slabs.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No slabs configured yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── PRICING CALCULATOR ── */}
        {adminTab === 'pricing' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🎉</span>
              <h2 className="text-xl font-bold text-gray-800">Festive Pricing Calculator</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">Enter a selling price to auto-generate MRP and discount.</p>
            <div className="flex flex-wrap gap-3 items-end mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Final Selling Price (₹)</label>
                <input type="number" value={sellingInput} onChange={e => setSellingInput(e.target.value)} placeholder="e.g. 200"
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-36 focus:ring-2 focus:ring-[#DAA520] focus:outline-none" />
              </div>
              <button onClick={handlePreview} className="bg-[#DAA520] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#c5941c]">Generate</button>
            </div>
            {previewPrice && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {[
                    { label: 'Selling Price', value: `₹${previewPrice.sellingPrice}`, color: 'text-[#E91E63]' },
                    { label: 'MRP', value: `₹${previewPrice.originalPrice}`, color: 'text-gray-500 line-through' },
                    { label: 'Discount', value: `${previewPrice.discountPercent}% OFF`, color: 'text-green-600' },
                    { label: 'Customer Saves', value: `₹${previewPrice.amountSaved}`, color: 'text-purple-600' },
                  ].map(c => (
                    <div key={c.label} className="bg-white rounded-lg p-3 text-center shadow-sm">
                      <p className="text-xs text-gray-400">{c.label}</p>
                      <p className={`text-lg font-bold ${c.color}`}>{c.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── WALLET REPORT ── */}
        {adminTab === 'report' && report && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Balance', value: `₹${Number(report.totalWalletBalance).toFixed(0)}` },
                { label: 'Cashback Given', value: `₹${Number(report.totalCashbackGiven).toFixed(0)}` },
                { label: 'Total Recharged', value: `₹${Number(report.totalRechargeAmount).toFixed(0)}` },
                { label: 'Total Wallet Payments', value: `₹${Number(report.totalWalletPayments).toFixed(0)}` },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
                  <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                  <p className="text-2xl font-bold text-[#1A1A2E]">{s.value}</p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-800">Top Wallet Users</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>{['User','Email','Balance','Total Added','Total Spent','Cashback'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.topUsers?.map((u: any) => (
                    <tr key={u.userId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{u.userName}</td>
                      <td className="px-4 py-3 text-gray-500">{u.email}</td>
                      <td className="px-4 py-3 font-bold text-green-600">₹{Number(u.balance).toFixed(2)}</td>
                      <td className="px-4 py-3">₹{Number(u.totalAdded).toFixed(2)}</td>
                      <td className="px-4 py-3">₹{Number(u.totalSpent).toFixed(2)}</td>
                      <td className="px-4 py-3 text-purple-600">₹{Number(u.cashbackEarned).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
